import type {MouseEvent, ReactNode} from "react";
import {forwardRef, useId} from "react";
import {colorThemes, elementThemes, subThemes} from "../../css/themes/themes.css";
import cx from "../../utils/cx";
import {DSIcon} from "../DSIcon/DSIcon";
import type {CSSProps} from "../Box/Box";
import {css, Text} from "../Box/Box";
import {useReactiveIcon} from "../DSButton/button-helpers";
import {isWithMetaKey} from "../../utils/device-utils";
import {DSCheckboxStyles as styles} from "./DSCheckbox.css";

export type DSCheckboxWithLabelProps = DSCheckboxProps & {
  label: ReactNode;
  labelTextType?: CSSProps["textType"];
};

export const DSCheckboxWithLabel = forwardRef<HTMLButtonElement, DSCheckboxWithLabelProps>(
  (props, ref) => {
    const {label, id: passedId, labelTextType = "label14", ...rest} = props;
    const fallbackId = useId();
    const id = passedId || fallbackId;
    const labelTextId = id ? `${id}-label` : undefined;

    return (
      <label className={css({sp: "8px", display: "flex", align: "start"})} htmlFor={id}>
        <DSCheckbox {...rest} id={id} ref={ref} aria-labelledby={labelTextId} />
        <Text id={labelTextId} type={labelTextType} color="primary">
          {label}
        </Text>
      </label>
    );
  }
);

export type DSCheckboxProps = {
  onChange?: (value: boolean) => unknown;
  onMetaClick?: (value: boolean) => void | Promise<void>;
  hasError?: boolean;
  disabled?: boolean;
  checked?: boolean;
  pending?: boolean;
  id?: string;
  className?: string;
  onClick?: (e: MouseEvent) => void;
  "aria-labelledby"?: string;
};

export const DSCheckbox = forwardRef<HTMLButtonElement, DSCheckboxProps>(
  (
    {onChange, hasError, disabled, checked, pending, onMetaClick, className, onClick, ...rest},
    ref
  ) => {
    const getClasses = (): string[] => {
      if (disabled) {
        return [elementThemes.neutral, colorThemes.elementThemeBase, css({bgWithOpacity: "0.2"})];
      }
      if (hasError) {
        if (checked) {
          return [elementThemes.alert, colorThemes.elementThemeBase];
        } else {
          return [subThemes.alert];
        }
      }
      if (checked) return [elementThemes.active, colorThemes.elementThemeBase];
      return [];
    };

    const {handleClick, reactiveIcon} = useReactiveIcon({
      iconEl: (
        <DSIcon
          name="Check"
          label="checked"
          size={null}
          className={cx(
            styles.icon.base,
            checked ? styles.icon.checked : styles.icon.unchecked,
            disabled ? styles.icon.disabled : styles.icon.nonDisabled
          )}
        />
      ),
      onClick: (e: MouseEvent) => {
        const clickFn = isWithMetaKey(e as any) && onMetaClick ? onMetaClick : onChange;
        return clickFn && clickFn(!checked);
      },
      passedState: pending ? "loading" : undefined,
      skipSuccessIcon: true,
    });

    return (
      <button
        type="button"
        onClick={(e) => {
          onClick?.(e);
          handleClick(e);
        }}
        className={css({rounded: 4}, styles.base, ...getClasses(), className)}
        disabled={disabled}
        role="checkbox"
        aria-checked={checked}
        aria-disabled={disabled}
        ref={ref}
        {...rest}
      >
        {reactiveIcon}
      </button>
    );
  }
);
